import { useState } from 'react';
import { B } from '../../constants/brand';
import { fmt } from '../../utils/format';
import { api } from '../../api/client';
import { useApi } from '../../hooks/useApi';
import { Btn, Input, Textarea, Select, Modal, ModalFooter, Table, Spinner, StatusBadge, useToast } from '../ui';

// ---------------------------------------------------------------------------
// DealProjectsPanel — a workstream with its own lifecycle.
//
// A Project is neither a DealDetail's capital type (a fact about the ASSET)
// nor a parent task's grouping (which has no status/owner/dates of its own —
// see "A PARENT TASK IS A GROUP" in CLAUDE.md). It is its own row: a name, a
// status, an optional lead and target dates. Tasks and notes tag themselves
// to a project as an ORTHOGONAL TAG — they still carry their own
// DealID/DealDetailID and still show up in the deal/detail's ordinary
// Notes & Tasks feed, unlike task-scoped notes.
//
// Pass dealDetailId to scope to a single detail (plus deal-level projects
// when includeDealLevel is set, mirroring DealTasksPanel/NotesFeed); omit it
// for the deal-level bucket only.
// ---------------------------------------------------------------------------

const EMPTY_FORM = {
  name: '', description: '', status: 'Planning', leadUserId: '', startDate: '', dueDate: '',
};

const projectToForm = (p) => ({
  name: p.name || '',
  description: p.description || '',
  status: p.status || 'Planning',
  leadUserId: p.leadUserID || '',
  startDate: p.startDate || '',
  dueDate: p.dueDate || '',
});

const ProjectFormModal = ({ form: initial, editing, users, statuses, busy, onClose, onSave }) => {
  const [f, setF] = useState(initial);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const statusOptions = (statuses && statuses.length ? statuses : ['Planning', 'Active', 'On Hold', 'Complete', 'Cancelled'])
    .map((s) => ({ value: s, label: s }));
  const leadOptions = [{ value: '', label: '— Unassigned —' }, ...(users || []).map((u) => ({ value: u.userID, label: u.displayName }))];
  return (
    <Modal title={editing ? 'Edit Project' : 'Add Project'} onClose={onClose} width={560}>
      <Input label="Name" value={f.name} onChange={(v) => set('name', v)} required autoFocus placeholder="e.g. Refinance the loan" />
      <Textarea label="Description" value={f.description} onChange={(v) => set('description', v)} rows={3} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Select label="Status" value={f.status} onChange={(v) => set('status', v)} options={statusOptions} />
        <Select label="Lead" value={f.leadUserId} onChange={(v) => set('leadUserId', v)} options={leadOptions} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Input label="Start Date" type="date" value={f.startDate} onChange={(v) => set('startDate', v)} />
        <Input label="Due Date" type="date" value={f.dueDate} onChange={(v) => set('dueDate', v)} />
      </div>
      <ModalFooter>
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        <Btn onClick={() => onSave(f)} loading={busy} disabled={!f.name.trim()}>Save</Btn>
      </ModalFooter>
    </Modal>
  );
};

export const DealProjectsPanel = ({ dealId, dealDetailId, users = [], includeDealLevel = false, onChanged }) => {
  const { toast } = useToast();
  const params = dealDetailId
    ? `dealDetailId=${dealDetailId}${includeDealLevel ? '&includeDealLevel=true' : ''}`
    : '';
  const { data, loading, reload } = useApi(
    () => api.get(`deals/${dealId}/projects${params ? `?${params}` : ''}`),
    [dealId, dealDetailId, includeDealLevel]
  );

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing]     = useState(null);
  const [form, setForm]           = useState(EMPTY_FORM);
  const [busy, setBusy]           = useState(false);

  if (loading) return <Spinner />;
  if (data && data.available === false) {
    return <div style={{ color: B.textLt, fontSize: 13, padding: '16px 0' }}>{data.reason || 'Projects are not available on this database yet.'}</div>;
  }

  const projects = data?.projects || [];
  const statuses = data?.statuses || [];
  const notifyChanged = () => onChanged?.();

  const openNew = () => { setEditing(null); setForm(EMPTY_FORM); setShowModal(true); };
  const openEdit = (p) => { setEditing(p); setForm(projectToForm(p)); setShowModal(true); };

  const save = async (f) => {
    setBusy(true);
    const body = {
      name: f.name.trim(),
      description: f.description || null,
      status: f.status,
      leadUserId: f.leadUserId || null,
      startDate: f.startDate || null,
      dueDate: f.dueDate || null,
      // A new project seeded from a specific detail tab is tagged to it; an
      // edit leaves dealDetailId untouched (the field isn't offered here —
      // re-scoping a project to a different detail isn't a form this panel
      // exposes, since it would silently move whatever tasks/notes are
      // already tagged to it out of the tab they're currently visible on).
      ...(editing ? {} : { dealDetailId: dealDetailId || null }),
    };
    try {
      if (editing) await api.put(`deals/${dealId}/projects/${editing.projectID}`, body);
      else await api.post(`deals/${dealId}/projects`, body);
      setShowModal(false);
      reload();
      notifyChanged();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? Tasks and notes tagged to it are kept — they simply lose the tag.`)) return;
    try {
      await api.del(`deals/${dealId}/projects/${p.projectID}`);
      reload();
      notifyChanged();
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
        <Btn size="sm" onClick={openNew}>+ Add Project</Btn>
      </div>

      {projects.length === 0 ? (
        <div style={{ color: B.textLt, fontSize: 13, textAlign: 'center', padding: '24px 0' }}>
          No projects yet. Click &ldquo;+ Add Project&rdquo; to start a workstream tasks and notes can be tagged to.
        </div>
      ) : (
        <Table
          columns={[
            { key: 'name',   label: 'Project', render: (v, row) => (
                <div>
                  <div style={{ fontWeight: 600 }}>{v}</div>
                  {row.description && <div style={{ fontSize: 12, color: B.textLt, marginTop: 2 }}>{row.description}</div>}
                </div>
              ) },
            { key: 'status', label: 'Status', render: (v) => <StatusBadge status={v} /> },
            { key: 'leadName', label: 'Lead', render: (v) => v || '—' },
            { key: '_dates', label: 'Timeline', render: (_, row) => [
                row.startDate ? fmt.date(row.startDate) : null,
                row.dueDate ? fmt.date(row.dueDate) : null,
              ].filter(Boolean).join(' – ') || '—' },
            { key: '_counts', label: 'Tasks / Notes', align: 'right', render: (_, row) => (
                <span style={{ color: B.textMd, fontSize: 12 }}>
                  {row.openTaskCount}/{row.taskCount} open · {row.noteCount} note{row.noteCount === 1 ? '' : 's'}
                </span>
              ) },
            { key: '_a', label: '', render: (_, row) => (
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <Btn size="sm" variant="ghost" onClick={() => openEdit(row)}>Edit</Btn>
                  <Btn size="sm" variant="ghost" onClick={() => remove(row)}>Delete</Btn>
                </div>
              ) },
          ]}
          data={projects}
        />
      )}

      {showModal && (
        <ProjectFormModal
          form={form}
          editing={!!editing}
          users={users}
          statuses={statuses}
          busy={busy}
          onClose={() => setShowModal(false)}
          onSave={save}
        />
      )}
    </div>
  );
};
